import { Component, NgZone, OnInit } from '@angular/core';
import {
    Router,
    Event,
    NavigationCancel,
    NavigationEnd,
    NavigationError,
    NavigationStart,
} from '@angular/router';
import { LimegearAuthenticationService } from './modules/login/services/limegear-authentication.service';
import { CurrentUserStateService } from './modules/shared/services/current-user-state.service';
import { DisplayOptionsService } from './modules/shared/services/display-options.service';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { LogoutIdleDialogComponent } from './modules/shared/components/logout-idle-dialog/logout-idle-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ImpersonationService } from 'app/modules/shared/services/impersonation.service';
import { ToastService } from 'app/modules/shared/services/toast.service';
import { UserManagementService } from 'app/modules/shared/services/user-management.service';
import { UserAccessService } from 'app/modules/shared/services/user-access.service';

declare let pendo: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
    loading = false;
    timeoutLength: 6000; // 3 seconds
    isFinishedLoading = false;

    lastPing?: Date = null;
    idleModalOpen = false;
    sideNavWidth: number;

    constructor(
        private _router: Router,
        private loginDetails: LimegearAuthenticationService,
        private _displayOptionsService: DisplayOptionsService,
        private currentUserStateService: CurrentUserStateService,
        private idle: Idle,
        private keepalive: Keepalive,
        public matDialog: MatDialog,
        private ngZone: NgZone,
        private impersonationService: ImpersonationService,
        private toastService: ToastService,
        private userManagementService: UserManagementService,
        private userAccessService: UserAccessService
    ) {
        _router.events.subscribe((e: Event) => {
            this.handleRouterLoading(e);
        });

        // Actions performed on user logged in
        this.currentUserStateService.accessAssigned.subscribe((res) => {
            if (res) {
                this._displayOptionsService.setDefaultDisplayOptions();
                this.resetIdle();

                let currentUrl = window.location.hostname;

                let debug = false; // Set this flag to enable pendo on local

                // Initialize Pendo if url is not localhost or test
                if ((!currentUrl.includes('localhost') && !currentUrl.includes('test')) || debug) {
                    let accountID =
                        this.currentUserStateService.currentUser.accountID ?? 'ACCOUNT-UNIQUE-ID';

                    let powerUser = this.currentUserStateService.isPowerUser() ?? false;
                    let platformUser = this.currentUserStateService.isPlatformUser() ?? false;
                    let superUser = this.currentUserStateService.isLBASuperUser() ?? false;
                    let baseClient = this.currentUserStateService.currentUser.baseClient ?? false;

                    if (debug) {
                        console.log('AccountID', accountID);
                        console.log('PowerUser: ' + powerUser);
                        console.log('PlatformUser: ' + platformUser);
                        console.log('SuperUser: ' + superUser);
                        console.log('BaseClient: ' + baseClient);
                    }

                    pendo.initialize({
                        visitor: {
                            id: this.currentUserStateService.currentUser.username,
                            nv_power_user: powerUser,
                            nv_platform_user: platformUser,
                            nv_super_user: superUser,
                            nv_base_client: baseClient,
                        },
                        account: {
                            id: accountID,
                            name: this.currentUserStateService.currentUser.clientFullName,
                            nv_base_client: baseClient,
                        },
                    });
                } else {
                    console.log(
                        'Pendo Initialization has been skipped due to localhost or test url.'
                    );
                }
            }
        });

        // Actions performed on user logged out
        this.currentUserStateService.accessRevoked.subscribe((res) => {
            if (res) {
                this.loginDetails.logout();

                this.matDialog.closeAll();
            }
        });

        // Actions performed before access token expires
        this.currentUserStateService.accessTokenExpiring.subscribe((res) => {
            if (res) {
                this.tryRefreshingAuthentication();
            }
        });

        this.loginDetails.onAuthSuccess().subscribe((userLoggedIn: boolean) => {
            if (userLoggedIn) {
                //executed once per succesful login
            }
        });

        // How long until user becomes idle (seconds)
        idle.setIdle(7200); // 2 hours = 7200

        // Timeout timer for idle modal (seconds)
        idle.setTimeout(60);

        // What actions to monitor for when determining idle status
        idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);

        // Once the user is active again
        idle.onIdleEnd.subscribe(() => {
            // console.log('LOG:', 'No longer idle');
            this.resetIdle();
            this.currentUserStateService.timeoutIdle = null;
        });

        // Once the user has become idle and the timeout timer has reached 0
        idle.onTimeout.subscribe(() => {
            //console.log(
            //    'LOG:',
            //    'Session timed out. You should be logged out now if this is working.'
            //);
            if (this.currentUserStateService.isLoggedIn()) {
                this.closeIdleModal();
                this.currentUserStateService.logout();
            }
        });

        // User has become idle; open logout modal
        idle.onIdleStart.subscribe(() => {
            // console.log('LOG:', 'Session is now idle. You should see idle modal. ');
            if (this.currentUserStateService.isLoggedIn()) {
                this.openIdleModal();
            }
        });

        // Update current user with timeout timer
        idle.onTimeoutWarning.subscribe((countdown) => {
            this.currentUserStateService.timeoutIdle = countdown;
        });

        // sets the ping interval to 15 seconds
        keepalive.interval(15);
        keepalive.onPing.subscribe(() => (this.lastPing = new Date()));
        this.resetIdle();
    }

    ngOnInit() {
        this.tryRefreshingAuthentication();
    }

    handleRouterLoading(event: Event): void {
        if (event instanceof NavigationStart) {
            this.loading = true;
        }

        if (
            event instanceof NavigationEnd ||
            event instanceof NavigationCancel ||
            event instanceof NavigationError
        ) {
            this.loading = false;
        }
    }

    resetIdle() {
        this.idle.watch();
    }

    private tryRefreshingAuthentication() {
        let initialLogin = !this.currentUserStateService.isLoggedIn();
        let windowLocation = window.location.pathname;
        let searchUrl = window.location.search;

        this.loginDetails.refreshToken().subscribe(
            (res) => {
                // Refresh token returned successfully w/ new JWT
                this.currentUserStateService.initCurrentUser(res.token);
                this.loginDetails.loginSetUser(res, initialLogin);
                this.isFinishedLoading = true;
            },
            (error) => {
                // Refresh token returned error, meaning the user needs to log in!
                if (this.currentUserStateService.isLoggedIn()) {
                    this.currentUserStateService.logout();
                }

                windowLocation = window.location.pathname;

                if (
                    windowLocation.includes('/password-reset') ||
                    windowLocation.includes('/login/callback/okta') ||
                    windowLocation.includes('/login/callback/openid') ||
                    windowLocation.includes('/login/portal') ||
                    windowLocation.includes('/login/connect/microsoft')
                ) {
                } else if (windowLocation != '/' && windowLocation != '/login') {
                    if (searchUrl && searchUrl.length != 0 && !searchUrl.includes('redirect')) {
                        windowLocation += searchUrl;
                    }

                    this._router.navigate(['/login'], {
                        queryParams: {
                            redirect: windowLocation,
                        },
                    });
                } else {
                    this._router.navigate(['']);
                }

                this.isFinishedLoading = true;
            }
        );
    }

    isLoggedIn(): boolean {
        return this.currentUserStateService?.isLoggedIn();
    }

    openIdleModal() {
        if (!this.idleModalOpen) {
            this.idleModalOpen = true;

            const dialogRef = this.matDialog.open(LogoutIdleDialogComponent, {
                disableClose: true,
            });

            dialogRef.afterClosed().subscribe((result) => {
                this.idleModalOpen = false;
                this.resetIdle();

                if (result) {
                    this.currentUserStateService.logout();
                }
            });
        }
    }

    toggleMenuButton(event: number) {
        this.sideNavWidth = event;
    }

    closeIdleModal() {
        this.matDialog.closeAll();
    }
}
